import { ArraySchema, filter, MapSchema, Schema, type } from "@colyseus/schema"

import { shuffleArraySchema } from "../../utils"
import { PlayerClass, PlayerDataClass } from "./player"
import type { CardColorsCommon } from "./сard"
import { CardDataClass } from "./сard"
import { defaultCardSrc as defaultSource } from "../../consts"

export type GameStatus = "waiting" | "playing" | "ended"

export class GameState extends Schema {
  @type("number") bet!: number

  @type("number") betCurrency!: number
  @type("number") betDecimals!: number

  @type("boolean") isPrivate!: boolean

  @type("number") createdAt!: number

  @type("number") minPlayers!: number
  @type("number") maxPlayers!: number

  @type("boolean") isDirectionClockwise!: boolean
  @type("string") status!: GameStatus

  @type("number") currentPlayer!: number
  @type("number") previousPlayer!: number

  @type("number") timerTime!: number
  @type("number") timer?: number

  @type("number") gameTimerEnd?: number
  @type("number") gameDuration!: number

  @type("string") gameId!: string

  @type("string") chatId?: string
  @type("string") chatTitle?: string
  @type("string") chatLang?: string

  @filter(function (): boolean {
    return false
  })
  @type([CardDataClass])
  availableCards = new ArraySchema<CardDataClass>()

  @filter(function (): boolean {
    return false
  })
  @type([CardDataClass])
  usedCards = new ArraySchema<CardDataClass>()

  @type([CardDataClass])
  threwCards = new ArraySchema<CardDataClass>()

  @type(CardDataClass) currentCardParams!: CardDataClass
  @type("string") chosenColor!: CardColorsCommon | undefined

  @type({ map: PlayerDataClass })
  players = new MapSchema<PlayerDataClass>()

  @type({ map: PlayerClass })
  visitors = new MapSchema<PlayerClass>()

  @type({ map: PlayerDataClass })
  lastPlayers = new MapSchema<PlayerDataClass>()

  getNextPlayer(): PlayerDataClass {
    const playersArray = Array.from(this.players.values())
    const nextPlayerIndex = this.findNextPlayerIndex(playersArray, 1) // Find next player skipping surrendered ones

    const player = this.players.get(
      String(playersArray[nextPlayerIndex].info.id)
    )
    if (!player) throw new Error("Player not found")

    return player
  }

  getPostNextPlayer(): PlayerDataClass {
    const playersArray = Array.from(this.players.values())
    const postNextPlayerIndex = this.findNextPlayerIndex(playersArray, 2) // Find post-next player skipping surrendered ones

    const player = this.players.get(
      String(playersArray[postNextPlayerIndex].info.id)
    )
    if (!player) throw new Error("Player not found")

    return player
  }

  findNextPlayerIndex(playersArray: PlayerDataClass[], steps: number) {
    let currentPlayerIndex = playersArray.findIndex(
      (player) => player.info.id === this.currentPlayer
    )
    let count = 0

    while (count < playersArray.length) {
      // Prevent infinite loop
      currentPlayerIndex =
        (currentPlayerIndex +
          (this.isDirectionClockwise ? 1 : -1) +
          playersArray.length) %
        playersArray.length
      if (!playersArray[currentPlayerIndex].surrenderAt) {
        steps -= 1
        if (steps === 0) break
      }
      count++
    }

    return currentPlayerIndex
  }

  getAvailableCards(
    quantity: number,
    custom = defaultSource
  ): ArraySchema<CardDataClass> {
    if (quantity > this.availableCards.length) {
      const usedCards = shuffleArraySchema(this.usedCards.clone())

      for (const card of usedCards)
        this.availableCards.push(
          new CardDataClass(card.cardColor, card.cardType)
        )

      this.usedCards.clear()
    }

    const cardsToRetrieve = this.availableCards.splice(0, quantity)

    const retrievedCards = new ArraySchema<CardDataClass>()
    for (const card of cardsToRetrieve) {
      this.usedCards.push(card.clone())
      retrievedCards.push(
        new CardDataClass(card.cardColor, card.cardType, custom)
      )
    }

    return retrievedCards
  }
}

export type GameType = InstanceType<typeof GameState>
